import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Plus, Shield, Search, X, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { AttachmentField } from "@/components/AttachmentField";
import { RulePanel } from "@/components/RulePanel";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";

type IncidentType = "NOISE" | "DAMAGE" | "TRESPASSING" | "THEFT" | "ACCIDENT" | "RULE_VIOLATION" | "OTHER";
type Status = "OPEN" | "INVESTIGATING" | "RESOLVED" | "CLOSED";

interface Incident {
  id: string;
  location: string;
  incidentType: IncidentType;
  description: string;
  unitInvolved?: string | null;
  photoUrls: string[];
  attachmentUrls: string[];
  status: Status;
  resolution?: string | null;
  reportedAt: string;
  resolvedAt?: string | null;
  reportedBy: { firstName: string; lastName: string; role: string };
}

interface SearchHit {
  id: string;
  location: string;
  incidentType: IncidentType;
  status: Status;
  unitInvolved: string | null;
  reportedAt: string;
  attachmentUrls: string[];
  reportedByFirstName: string;
  reportedByLastName: string;
  /** Escaped server-side; only <mark> survives as markup. */
  snippet: string;
}

/** Cloudinary keeps the original filename in the path, which is what a reader recognises. */
function fileName(url: string): string {
  try {
    return decodeURIComponent(new URL(url).pathname.split("/").pop() || url);
  } catch {
    return url;
  }
}

function AttachmentLinks({ urls }: { urls: string[] }) {
  if (urls.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {urls.map((u, idx) => (
        <a
          key={idx}
          href={u}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-primary hover:underline max-w-xs"
        >
          <FileText size={13} className="shrink-0" />
          <span className="truncate">{fileName(u)}</span>
        </a>
      ))}
    </div>
  );
}

const statusVariant: Record<Status, "warning" | "info" | "success" | "default"> = {
  OPEN: "warning",
  INVESTIGATING: "info",
  RESOLVED: "success",
  CLOSED: "default",
};

const STATUS_OPTIONS: Status[] = ["OPEN", "INVESTIGATING", "RESOLVED", "CLOSED"];

export function IncidentsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editIncident, setEditIncident] = useState<Incident | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Debounced so typing does not fire a query per keystroke, matching DocumentsPage.
  useEffect(() => {
    const id = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const searching = searchQuery.length >= 2;

  const { data: incidents = [], isLoading, isError } = useQuery({
    queryKey: ["incidents"],
    queryFn: async () => (await api.get<Incident[]>("/incidents")).data,
  });

  const { data: searchHits = [], isLoading: searchLoading } = useQuery({
    queryKey: ["incidents", "search", searchQuery],
    enabled: searching,
    queryFn: async () =>
      (await api.get<SearchHit[]>(`/incidents/search?q=${encodeURIComponent(searchQuery)}`)).data,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status, resolution }: { id: string; status: Status; resolution?: string }) =>
      (await api.put(`/incidents/${id}`, { status, resolution })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incidents"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/incidents/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incidents"] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield size={24} /> {t("security.incidents")}
        </h1>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> {t("security.newIncident")}
        </button>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="search"
          className="input w-full pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none"
          placeholder={t("security.searchIncidents")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput("")}
            aria-label={t("security.searchClear")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {searching && (
        searchLoading ? (
          <div className="text-slate-500">{t("common.loading")}</div>
        ) : searchHits.length === 0 ? (
          <div className="card text-slate-500">{t("security.searchNoResults", { query: searchQuery })}</div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              {t("security.searchResultsCount", { count: searchHits.length })}
            </p>
            {searchHits.map((hit) => (
              <div key={hit.id} className="card">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Badge variant="danger">{t(`security.incidentTypes.${hit.incidentType}`)}</Badge>
                  <Badge variant={statusVariant[hit.status]}>{t(`security.incidentStatus.${hit.status}`)}</Badge>
                  <span className="text-xs text-slate-500">· {hit.location}</span>
                  {hit.unitInvolved && <span className="text-xs text-slate-500">· Unidad {hit.unitInvolved}</span>}
                </div>
                {/* Escaped server-side; only <mark> survives as markup. */}
                <p
                  className="text-sm text-slate-600 leading-relaxed [&_mark]:bg-amber-200 [&_mark]:text-slate-900 [&_mark]:rounded [&_mark]:px-0.5"
                  dangerouslySetInnerHTML={{ __html: hit.snippet }}
                />
                <div className="text-xs text-slate-500 mt-2">
                  {hit.reportedByFirstName} {hit.reportedByLastName} · {formatDate(hit.reportedAt, i18n.language)}
                </div>
                <AttachmentLinks urls={hit.attachmentUrls ?? []} />
              </div>
            ))}
          </div>
        )
      )}

      {searching ? null : isError ? (
        <QueryError />
      ) : isLoading ? (
        <div className="text-slate-500">{t("common.loading")}</div>
      ) : incidents.length === 0 ? (
        <div className="card text-slate-500">—</div>
      ) : (
        <div className="space-y-4">
          {incidents.map((i) => (
            <div key={i.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant="danger">{t(`security.incidentTypes.${i.incidentType}`)}</Badge>
                    <Badge variant={statusVariant[i.status]}>{t(`security.incidentStatus.${i.status}`)}</Badge>
                    <span className="text-xs text-slate-500">· {i.location}</span>
                    {i.unitInvolved && <span className="text-xs text-slate-500">· Unidad {i.unitInvolved}</span>}
                  </div>
                  <p className="text-sm text-slate-700 mt-1">{i.description}</p>
                  <div className="text-xs text-slate-500 mt-2">
                    Reportado por {i.reportedBy.firstName} {i.reportedBy.lastName} · {formatDate(i.reportedAt, i18n.language)}
                  </div>
                  {i.photoUrls.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {i.photoUrls.map((u, idx) => (
                        <a key={idx} href={u} target="_blank" rel="noreferrer">
                          <img src={u} alt="" className="w-16 h-16 object-cover rounded border border-slate-200" />
                        </a>
                      ))}
                    </div>
                  )}
                  <AttachmentLinks urls={i.attachmentUrls ?? []} />
                  {i.resolution && (
                    <div className="mt-2 p-2 bg-green-50 rounded text-sm">
                      <strong>{t("security.resolution")}:</strong> {i.resolution}
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex flex-col items-end gap-2">
                  <select
                    className="input text-xs"
                    value={i.status}
                    onChange={(e) => {
                      const status = e.target.value as Status;
                      let resolution: string | undefined;
                      if (status === "RESOLVED" || status === "CLOSED") {
                        const r = prompt(t("security.resolution") + ":");
                        if (r) resolution = r;
                      }
                      updateMutation.mutate({ id: i.id, status, resolution });
                    }}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{t(`security.incidentStatus.${s}`)}</option>
                    ))}
                  </select>
                  <AdminRecordActions
                    compact
                    onEdit={() => setEditIncident(i)}
                    onDelete={() => deleteMutation.mutate(i.id)}
                    entityLabel="el incidente"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <NewIncidentModal open={open} onClose={() => setOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["incidents"] })} />
      {editIncident && (
        <NewIncidentModal
          key={editIncident.id}
          open
          incident={editIncident}
          onClose={() => setEditIncident(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ["incidents"] })}
        />
      )}
    </div>
  );
}

function NewIncidentModal({ open, incident, onClose, onDone }: { open: boolean; incident?: Incident | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const isEdit = !!incident;
  const [form, setForm] = useState({
    location: incident?.location ?? "",
    incidentType: (incident?.incidentType ?? "OTHER") as IncidentType,
    description: incident?.description ?? "",
    unitInvolved: incident?.unitInvolved ?? "",
  });
  const [photoUrls, setPhotoUrls] = useState<string[]>(incident?.photoUrls ?? []);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>(incident?.attachmentUrls ?? []);
  const { data: units = [] } = useQuery({
    queryKey: ["units-picker"],
    queryFn: async () => (await api.get<{ id: string; unitNumber: string }[]>("/units")).data,
    enabled: open,
  });
  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        unitInvolved: form.unitInvolved || null,
        photoUrls: photoUrls.filter(Boolean),
        attachmentUrls: attachmentUrls.filter(Boolean),
      };
      if (isEdit && incident) return (await api.put(`/incidents/${incident.id}`, body)).data;
      return (await api.post("/incidents", body)).data;
    },
    onSuccess: () => {
      onDone();
      onClose();
      if (!isEdit) {
        setForm({ location: "", incidentType: "OTHER", description: "", unitInvolved: "" });
        setPhotoUrls([]);
        setAttachmentUrls([]);
      }
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t("common.edit") : t("security.newIncident")} size="lg">
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("security.type")}</label>
            <select className="input" value={form.incidentType} onChange={(e) => setForm({ ...form, incidentType: e.target.value as IncidentType })}>
              {(["NOISE", "DAMAGE", "TRESPASSING", "THEFT", "ACCIDENT", "RULE_VIOLATION", "OTHER"] as IncidentType[]).map((c) => (
                <option key={c} value={c}>{t(`security.incidentTypes.${c}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">{t("security.location")}</label>
            <input className="input" required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">{t("security.unitInvolved")}</label>
          <select className="input" value={form.unitInvolved} onChange={(e) => setForm({ ...form, unitInvolved: e.target.value })}>
            <option value="">—</option>
            {units.map((u) => (
              <option key={u.id} value={u.unitNumber}>{u.unitNumber}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">{t("security.description")}</label>
          <textarea className="input min-h-[120px]" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        {form.incidentType === "RULE_VIOLATION" && (
          <RulePanel tags={["fine", "incident"]} defaultOpen />
        )}
        <AttachmentField
          label={t("security.photos")}
          folder="costadorada1/incidents"
          urls={photoUrls}
          onChange={setPhotoUrls}
          accept=".jpg,.jpeg,.png,.heic,.webp"
          addLabel={t("security.addPhoto")}
        />
        <AttachmentField
          label={t("security.attachments")}
          folder="costadorada1/incidents"
          urls={attachmentUrls}
          onChange={setAttachmentUrls}
          accept=".pdf,.doc,.docx"
          addLabel={t("security.addAttachment")}
        />
        {mutation.isError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {(mutation.error as AxiosError<{ message: string }>).response?.data?.message ?? t("common.error")}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn-primary" disabled={mutation.isPending}>{t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}
